<template>
  <div class="rqa_list_view">
    <div class="rqa_tabs" role="tablist" aria-label="סינון פניות">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        role="tab"
        class="rqa_tab"
        :class="{ rqa_tab_active: activeTab === tab.value }"
        :aria-selected="activeTab === tab.value ? 'true' : 'false'"
        @click="selectTab(tab.value)">
        {{ tab.label }}
        <span v-if="tabHasUnread(tab.value)" class="rqa_dot rqa_tab_dot" role="img" aria-label="יש פניות עם עדכון שלא נקרא"></span>
      </button>
    </div>

    <div ref="scroller" class="rqa_list">
      <p v-if="meta.loading && !tickets.length" class="rqa_muted rqa_center" role="status">טוענת פניות…</p>
      <div v-else-if="meta.error && !tickets.length" class="rqa_center">
        <p class="rqa_error" role="alert">{{ meta.error }}</p>
        <button type="button" class="button is-small" @click="reload">טעינה מחדש</button>
      </div>
      <p v-else-if="meta.loaded && !tickets.length" class="rqa_muted rqa_center">{{ emptyText }}</p>

      <ul v-if="tickets.length" class="rqa_rows">
        <li v-for="t in tickets" :key="t.root_id">
          <button
            type="button"
            class="rqa_row"
            :class="{ rqa_row_unread: t.unread }"
            :data-root="t.root_id"
            @click="open(t.root_id)">
            <span class="rqa_row_main">
              <span class="rqa_row_title">{{ titleOf(t) }}</span>
              <span class="rqa_row_meta">
                <recruiter-qa-status-pill v-if="statusOf(t)" :status="statusOf(t)" />
                <span class="rqa_muted rqa_small">{{ timeOf(t) }}</span>
                <span v-if="t.jira_task_ids && t.jira_task_ids.length" class="rqa_tag">משימה</span>
              </span>
            </span>
            <span v-if="t.unread" class="rqa_dot" role="img" aria-label="עדכון שלא נקרא"></span>
          </button>
        </li>
      </ul>

      <div v-if="hasMoreTickets" class="rqa_center">
        <button type="button" class="button is-small" :class="{ 'is-loading': meta.loading }" @click="more">הצג עוד</button>
        <p v-if="meta.error && tickets.length" class="rqa_error" role="alert">{{ meta.error }}</p>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import RecruiterQaStatusPill from './RecruiterQaStatusPill'
import { ticketTitle, relativeTime, recruiterTicketStatus } from '@/utils/recruiterQaFormat'

export default {
  name: 'RecruiterQaTicketList',
  components: { RecruiterQaStatusPill },
  data () {
    return {
      tabs: [{ value: 'open', label: 'פתוחות' }, { value: 'closed', label: 'סגורות' }],
      now: Date.now(),
      clockTimer: null
    }
  },
  computed: {
    ...mapState('recruiterQa', ['activeTab', 'listMeta', 'listScroll', 'unreadCounts', 'selectedRootId']),
    ...mapGetters('recruiterQa', ['ticketsFor', 'hasMore', 'answerRefresh']),
    tickets () { return this.ticketsFor(this.activeTab) },
    meta () { return this.listMeta[this.activeTab] },
    hasMoreTickets () { return this.hasMore(this.activeTab) },
    emptyText () { return this.activeTab === 'closed' ? 'אין פניות סגורות' : 'אין פניות פתוחות' }
  },
  mounted () {
    this.clockTimer = setInterval(() => { this.now = Date.now() }, 60000)
    this.$nextTick(this.restore)
  },
  beforeDestroy () {
    clearInterval(this.clockTimer)
    this.saveScroll()
  },
  methods: {
    statusOf (t) { return recruiterTicketStatus(t.latest_status, !!this.answerRefresh(t.root_id)) },
    tabHasUnread (tab) { return (this.unreadCounts[tab] || 0) > 0 },
    titleOf (t) { return ticketTitle(t.title) || '#' + t.root_id },
    timeOf (t) { return relativeTime(t.last_activity_at, this.now) },
    saveScroll () {
      const el = this.$refs.scroller
      if (el) this.$store.commit('recruiterQa/SET_LIST_SCROLL', { tab: this.activeTab, top: el.scrollTop })
    },
    // Restore scroll for this tab and focus the row we came back from.
    restore () {
      const el = this.$refs.scroller
      if (!el) return
      el.scrollTop = this.listScroll[this.activeTab] || 0
      if (this.selectedRootId) {
        const row = el.querySelector('[data-root="' + this.selectedRootId + '"]')
        if (row && row.focus) row.focus({ preventScroll: true })
      }
    },
    async selectTab (tab) {
      if (tab === this.activeTab) return
      this.saveScroll()
      const pending = this.$store.dispatch('recruiterQa/setTab', { tab })
      this.$nextTick(this.restore)
      await pending
    },
    reload () { this.$store.dispatch('recruiterQa/loadTickets', { tab: this.activeTab }) },
    more () { this.$store.dispatch('recruiterQa/loadTickets', { tab: this.activeTab, more: true }) },
    open (rootId) {
      this.saveScroll()
      this.$store.dispatch('recruiterQa/openTicket', { rootId })
    }
  }
}
</script>
<style scoped>
.rqa_list_view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.rqa_tabs {
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding: 0 16px;
}
.rqa_tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 10px 12px;
  min-height: 40px;
  font-size: 14px;
  color: #555;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}
.rqa_tab_active {
  color: #3273dc;
  border-bottom-color: #3273dc;
  font-weight: bold;
}
.rqa_tab_dot {
  margin-right: 6px;
}
.rqa_list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 16px 16px;
}
.rqa_rows {
  list-style: none;
  margin: 0;
  padding: 0;
}
.rqa_row {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: right;
  background: #fff;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 10px 4px;
  min-height: 56px;
  cursor: pointer;
  color: #333;
  font: inherit;
}
.rqa_row:hover, .rqa_row:focus {
  background-color: #f6f9fe;
}
.rqa_row_main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.rqa_row_title {
  font-size: 14px;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}
.rqa_row_unread .rqa_row_title {
  font-weight: bold;
}
.rqa_row_meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 4px;
}
.rqa_row_meta > * {
  margin-left: 8px;
}
.rqa_tag {
  font-size: 11px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  padding: 0 6px;
  color: #555;
}
.rqa_dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: #3273dc;
  flex: none;
}
.rqa_muted {
  color: #888;
}
.rqa_small {
  font-size: 12px;
}
.rqa_center {
  text-align: center;
  margin-top: 20px;
}
.rqa_error {
  color: #c9302c;
  font-size: 12px;
  margin: 6px 0;
}
@media (max-width: 767px) {
  .rqa_tab { min-height: 44px; flex: 1; justify-content: center; }
  .rqa_row { min-height: 64px; }
}
</style>
